<template>
    <div class="text-base text-gray-600 ml-4 mr-4 flex items-center cursor-pointer hover:text-gray-700" @click="showDialog=true">
        <font-awesome-icon :icon="['fas', 'user-check']" /> <div class="ml-2">Assign to</div>

        <jet-dialog-modal
            v-if="showDialog"
            :show="showDialog"
            @close="showDialog = false"
        >
            <template #title>
                Assign {{ selectedNoun }} to user
            </template>

            <template #content>
                <div class="mb-6">
                    Which user would you like to assign {{ contactText }} to?
                </div>

                <div v-if="loadingUsers" class="text-gray-500">
                    Loading users...
                </div>

                <div v-else class="space-y-4">
                    <!-- Assign to me card -->
                    <label class="block cursor-pointer">
                        <div class="p-4 border-2 rounded-lg hover:bg-gray-50 transition-colors"
                             :class="{ 'border-blue-500 bg-blue-50': assignmentType === 'me' }">
                            <div class="flex items-center">
                                <input
                                    type="radio"
                                    value="me"
                                    v-model="assignmentType"
                                    class="mr-3"
                                />
                                <div>
                                    <div class="font-semibold text-lg">Assign to me</div>
                                    <div class="text-sm text-gray-600">Assign {{ contactText }} to yourself</div>
                                </div>
                            </div>
                        </div>
                    </label>

                    <!-- Assign to someone else card (only show if there are other users) -->
                    <label v-if="otherUsers.length > 0" class="block cursor-pointer">
                        <div class="p-4 border-2 rounded-lg hover:bg-gray-50 transition-colors"
                             :class="{ 'border-blue-500 bg-blue-50': assignmentType === 'other' }">
                            <div class="flex items-center mb-3">
                                <input
                                    type="radio"
                                    value="other"
                                    v-model="assignmentType"
                                    class="mr-3"
                                />
                                <div>
                                    <div class="font-semibold text-lg">Assign to...</div>
                                    <div class="text-sm text-gray-600">Choose another team member</div>
                                </div>
                            </div>

                            <div v-if="assignmentType === 'other'" class="ml-6">
                                <select
                                    v-model="selectedUserTraId"
                                    class="w-full p-2 border rounded-md bg-white"
                                    :disabled="assignmentType !== 'other'"
                                >
                                    <option value="">Select a user...</option>
                                    <option
                                        v-for="user in otherUsers"
                                        :key="user.tra_id"
                                        :value="user.tra_id"
                                    >
                                        {{ user.name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </label>

                    <!-- Unassigned card -->
                    <label class="block cursor-pointer">
                        <div class="p-4 border-2 rounded-lg hover:bg-gray-50 transition-colors"
                             :class="{ 'border-blue-500 bg-blue-50': assignmentType === 'unassigned' }">
                            <div class="flex items-center">
                                <input
                                    type="radio"
                                    value="unassigned"
                                    v-model="assignmentType"
                                    class="mr-3"
                                />
                                <div>
                                    <div class="font-semibold text-lg">Unassigned</div>
                                    <div class="text-sm text-gray-600">Remove assignment from {{ contactText }}</div>
                                </div>
                            </div>
                        </div>
                    </label>
                </div>
            </template>

            <template #footer>
                <button @click="modalClose" class="mr-4">
                    Cancel
                </button>

                <jet-button
                    v-if="!loadingUsers"
                    @click.native="assignContacts"
                    :disabled="loading || (assignmentType === 'other' && !selectedUserTraId)"
                >
                    {{ loading ? 'Assigning...' : buttonText }}
                </jet-button>
            </template>
        </jet-dialog-modal>
    </div>
</template>

<script>
import {mapWritableState, mapActions} from 'pinia';
import { useGeneralTableStore } from '@/Stores/general_table';

export default {
    name: 'ContactTableActionAssignTo',
    data: () => ({
        showDialog: false,
        assignmentType: 'me', // 'me', 'other', 'unassigned'
        selectedUserTraId: null,
        users: [],
        loading: false,
        loadingUsers: false,
        currentUser: null,
    }),
    computed: {
        ...mapWritableState(useGeneralTableStore, ['selected', 'selectedFormatted', 'selectedNoun']),
        otherUsers() {
            return this.users.filter(user => user.tra_id !== this.currentUser?.tra_id);
        },
        selectedUser() {
            if (this.assignmentType === 'me') {
                return this.currentUser;
            } else if (this.assignmentType === 'other') {
                return this.users.find(user => user.tra_id === this.selectedUserTraId) || null;
            }
            return null;
        },
        finalUserTraId() {
            if (this.assignmentType === 'me') {
                return this.currentUser?.tra_id;
            } else if (this.assignmentType === 'other') {
                return this.selectedUserTraId;
            }
            return null; // unassigned
        },
        contactText() {
            return this.selectedNoun === 'contact' ? 'this contact' : 'these contacts';
        },
        buttonText() {
            let action;
            if (this.assignmentType === 'me') {
                action = 'Assign to me';
            } else if (this.assignmentType === 'other') {
                action = this.selectedUser ? `Assign to ${this.selectedUser.name}` : 'Assign to...';
            } else {
                action = 'Mark as unassigned';
            }
            return `${action} (${this.selectedFormatted} ${this.selectedNoun})`;
        }
    },
    methods: {
        ...mapActions(useGeneralTableStore, ['clearData', 'fetchData']),

        async loadUsers() {
            this.loadingUsers = true;
            try {
                const response = await axios.get(route('organization.users'));
                this.users = response.data;
                this.currentUser = this.$page.props.auth.user;
            } catch (error) {
                this.$toasted.show('Failed to load users', {
                    position: 'bottom-right',
                    type: 'error',
                    duration: 2000,
                });
            } finally {
                this.loadingUsers = false;
            }
        },

        async assignContacts() {
            // Validate selection for 'other' type
            if (this.assignmentType === 'other' && !this.selectedUserTraId) {
                this.$toasted.show('Please select a user from the dropdown', {
                    position: 'bottom-right',
                    type: 'error',
                    duration: 2000,
                });
                return;
            }

            this.loading = true;
            try {
                await axios.post(route('contacts.assignTo'), {
                    tra_ids: this.selected.map(item => item.tra_id),
                    user_tra_id: this.finalUserTraId
                });

                let assignedToText;
                if (this.assignmentType === 'me') {
                    assignedToText = 'to you';
                } else if (this.assignmentType === 'other') {
                    assignedToText = `to ${this.selectedUser.name}`;
                } else {
                    assignedToText = 'as unassigned';
                }

                this.$toasted.show(`${this.selectedFormatted} ${this.selectedNoun} assigned ${assignedToText}`, {
                    position: 'bottom-right',
                    type: 'success',
                    duration: 2000,
                });

                this.clearData();
                this.fetchData();
                this.modalClose();
            } catch (error) {
                this.$toasted.show('An error occurred while assigning contacts', {
                    position: 'bottom-right',
                    type: 'error',
                    duration: 2000,
                });
            } finally {
                this.loading = false;
            }
        },

        modalClose() {
            this.showDialog = false;
            this.assignmentType = 'me';
            this.selectedUserTraId = null;
        }
    },

    watch: {
        showDialog(newVal) {
            if (newVal) {
                this.loadUsers();
            }
        },
        assignmentType(newVal) {
            if (newVal !== 'other') {
                this.selectedUserTraId = null;
            }
        }
    }
}
</script>